import React from "react";
import { action, makeObservable, observable } from "mobx";
import { MrbBaseListViewStore } from "mrb/core";
import { MrbFilterParameters } from "mrb/core/filter";
import { MrbTableViewStore, MrbQuerySelectStore } from "mrb/components";
import { DownloadRequest } from "common/models";

function formatBytes(bytes) {
    if (!bytes) return "—";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

function deunderscore(value) {
    return value ? value.replace(/_/g, " ") : "—";
}

class UploadedContentFilter extends MrbFilterParameters {
    @observable producer = null;
    @observable artist = null;
    @observable album = null;

    constructor() {
        super();
        makeObservable(this);
    }

    reset() {
        super.reset();
        this.producer = null;
        this.artist = null;
        this.album = null;
    }
}

class UploadedContentViewStore extends MrbBaseListViewStore {
    constructor(rootStore, { routeStore }) {
        super(rootStore, {
            queryConfig: {
                filter: new UploadedContentFilter(),
                orderBy: "uploadedAt",
                orderDirection: "desc",
            },
        });
        this.routeStore = routeStore;
        makeObservable(this);

        const self = this;

        this.producerSelectStore = new MrbQuerySelectStore(
            {
                placeholder: "All producers",
                isSearchable: true,
                fetchOnChange: false,
                virtualized: false,
                textKey: "name",
                valueKey: "name",
                initFetch: true,
                isMulti: true,
                actions: {
                    find: async (searchTerm) => {
                        const opts = await self.routeStore.getFilterOptions();
                        const items = (opts.producers || []).filter(
                            (p) => !searchTerm || p.name.toLowerCase().includes(searchTerm.toLowerCase())
                        );
                        return { item: items, page: 1, recordsPerPage: items.length, totalRecords: items.length };
                    },
                    mapItem: (item) => ({ value: item.name, label: deunderscore(item.name) }),
                },
            },
            this.queryUtility,
            "producer"
        );

        this.artistSelectStore = new MrbQuerySelectStore(
            {
                placeholder: "All artists",
                isSearchable: true,
                fetchOnChange: false,
                virtualized: false,
                textKey: "name",
                valueKey: "name",
                initFetch: true,
                isMulti: true,
                actions: {
                    find: async (searchTerm) => {
                        const opts = await self.routeStore.getFilterOptions();
                        const items = (opts.artists || []).filter(
                            (a) => !searchTerm || a.name.toLowerCase().includes(searchTerm.toLowerCase())
                        );
                        return { item: items, page: 1, recordsPerPage: items.length, totalRecords: items.length };
                    },
                    mapItem: (item) => ({ value: item.name, label: deunderscore(item.name) }),
                },
            },
            this.queryUtility,
            "artist"
        );

        this.albumSelectStore = new MrbQuerySelectStore(
            {
                placeholder: "All albums",
                isSearchable: true,
                fetchOnChange: false,
                virtualized: false,
                textKey: "name",
                valueKey: "name",
                initFetch: true,
                isMulti: true,
                actions: {
                    find: async (searchTerm) => {
                        const opts = await self.routeStore.getFilterOptions();
                        const items = (opts.albums || []).filter(
                            (a) => !searchTerm || a.name.toLowerCase().includes(searchTerm.toLowerCase())
                        );
                        return { item: items, page: 1, recordsPerPage: items.length, totalRecords: items.length };
                    },
                    mapItem: (item) => ({ value: item.name, label: deunderscore(item.name) }),
                },
            },
            this.queryUtility,
            "album"
        );

        this.setGridStore(
            new MrbTableViewStore(rootStore, this.queryUtility, {
                columns: [
                    {
                        key: "producer",
                        title: "ADMINISTRATION.UPLOADED_CONTENT.LIST.COLUMNS.PRODUCER",
                        format: (value) => deunderscore(value),
                        sortable: true,
                        width: 160,
                    },
                    {
                        key: "artist",
                        title: "ADMINISTRATION.UPLOADED_CONTENT.LIST.COLUMNS.ARTIST",
                        format: (value) => deunderscore(value),
                        sortable: true,
                        width: 160,
                    },
                    {
                        key: "album",
                        title: "ADMINISTRATION.UPLOADED_CONTENT.LIST.COLUMNS.ALBUM",
                        format: (value) => deunderscore(value),
                        sortable: true,
                        width: 160,
                    },
                    {
                        key: "uploadedAt",
                        title: "ADMINISTRATION.UPLOADED_CONTENT.LIST.COLUMNS.UPLOAD_DATE",
                        format: (value) => (value ? new Date(value).toLocaleString() : "—"),
                        sortable: true,
                        width: 200,
                    },
                    {
                        key: "totalSize",
                        title: "ADMINISTRATION.UPLOADED_CONTENT.LIST.COLUMNS.SIZE",
                        format: (value) => formatBytes(value),
                        sortable: true,
                        width: 110,
                    },
                    {
                        key: "fileCount",
                        title: "ADMINISTRATION.UPLOADED_CONTENT.LIST.COLUMNS.FILE_COUNT",
                        sortable: false,
                        width: 90,
                    },
                    {
                        key: "_download",
                        title: "",
                        sortable: false,
                        width: 120,
                        suppressSizeToFit: true,
                        cell: (params) =>
                            React.createElement(
                                "button",
                                {
                                    className: "c-btn c-btn--sml c-btn--ghost",
                                    onClick: (e) => {
                                        e.stopPropagation();
                                        self.downloadSession(params.data);
                                    },
                                },
                                "Download"
                            ),
                    },
                ],
                onRowClick: action((event) => {
                    if (event.column && event.column.getColId() === "_download") return;
                    self.openSession(event.data);
                }),
            })
        );
    }

    find(params) {
        return this.routeStore.getSessions(params);
    }

    onFetchError(err) {
        this.rootStore.notificationStore.error(
            "An unexpected error occurred while loading upload sessions.",
            err
        );
    }

    @action.bound
    openSession(session) {
        this.rootStore.routerStore.goTo(
            "master.administration.uploaded-content.session",
            { sessionId: session.sessionId }
        );
    }

    @action.bound
    async downloadSession(session) {
        try {
            let page = 1;
            const rpp = 200;
            let totalRecords = Infinity;
            const allItems = [];

            while ((page - 1) * rpp < totalRecords) {
                const result = await this.routeStore.getSessionFiles(session.sessionId, {
                    pageNumber: page,
                    pageSize: rpp,
                });
                totalRecords = result.totalRecords;
                allItems.push(...result.item);
                page++;
            }

            const requests = allItems.map(
                (file) =>
                    new DownloadRequest(
                        file.key,
                        file.fileName,
                        null,
                        this.routeStore.createDownloadUrl(file.key),
                        null,
                        file.size ?? null
                    )
            );
            this.rootStore.downloadManagerStore.addItemsToQueue(requests);
        } catch (err) {
            this.rootStore.notificationStore.error("Failed to prepare session downloads.", err);
        }
    }
}

export { UploadedContentViewStore };
